import { useParams, useNavigate } from "react-router-dom";
import { useCustomerProjectDetail, useCustomerInvoices, useCustomerChangeOrders, useCustomerDocuments } from "@/hooks/useCustomerPortal";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, MapPin, Calendar } from "lucide-react";

const fmt = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

const CustomerProjectDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: project, isLoading } = useCustomerProjectDetail(id);
  const { data: invoices } = useCustomerInvoices(id);
  const { data: changeOrders } = useCustomerChangeOrders(id);
  const { data: documents } = useCustomerDocuments(id);

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>;
  }

  if (!project) {
    return <div className="py-12 text-center text-muted-foreground">Project not found</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/portal/customer/projects")}><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold tracking-tight">{project.name}</h1>
          <p className="text-sm text-muted-foreground">{project.project_number}</p>
        </div>
        <Badge variant="outline" className="capitalize">{project.status}</Badge>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {(project.city || project.state || project.address) && (
          <Card>
            <CardContent className="p-4 flex items-start gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div className="text-sm">
                {project.address && <p>{project.address}</p>}
                <p className="text-muted-foreground">{[project.city, project.state].filter(Boolean).join(", ")}</p>
              </div>
            </CardContent>
          </Card>
        )}
        {project.start_date && (
          <Card>
            <CardContent className="p-4 flex items-start gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div className="text-sm">
                <p className="text-muted-foreground">Start Date</p>
                <p className="font-medium">{project.start_date}</p>
              </div>
            </CardContent>
          </Card>
        )}
        {project.scheduled_completion && (
          <Card>
            <CardContent className="p-4 flex items-start gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div className="text-sm">
                <p className="text-muted-foreground">Target Completion</p>
                <p className="font-medium">{project.scheduled_completion}</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {project.description && (
        <Card><CardContent className="p-4 text-sm">{project.description}</CardContent></Card>
      )}

      <Tabs defaultValue="invoices">
        <TabsList>
          <TabsTrigger value="invoices">Invoices ({(invoices ?? []).length})</TabsTrigger>
          <TabsTrigger value="change-orders">Change Orders ({(changeOrders ?? []).length})</TabsTrigger>
          <TabsTrigger value="documents">Documents ({(documents ?? []).length})</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices" className="mt-4">
          {(invoices ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No invoices shared for this project</p>
          ) : (
            <div className="space-y-2">
              {(invoices ?? []).map((inv) => (
                <Card key={inv.id}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{inv.invoice_number}</p>
                      <p className="text-xs text-muted-foreground">{inv.invoice_date}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-sm">{fmt(inv.total)}</p>
                      <Badge variant="outline" className="text-xs capitalize">{inv.status}</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="change-orders" className="mt-4">
          {(changeOrders ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No change orders shared for this project</p>
          ) : (
            <div className="space-y-2">
              {(changeOrders ?? []).map((co) => (
                <Card key={co.id}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">CO #{co.number}</p>
                      <p className="text-xs text-muted-foreground line-clamp-1">{co.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-sm">{fmt(co.amount)}</p>
                      <Badge variant="outline" className="text-xs capitalize">{co.status}</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          {(documents ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No documents shared for this project</p>
          ) : (
            <div className="space-y-2">
              {(documents ?? []).map((doc) => (
                <Card key={doc.id}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{doc.title}</p>
                      <p className="text-xs text-muted-foreground">{doc.file_name}</p>
                    </div>
                    <p className="text-xs text-muted-foreground">{doc.created_at?.split("T")[0]}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CustomerProjectDetail;
